import { useMutation, useQueryClient } from "@tanstack/react-query";
import { merchantKeys } from "Api/Queries/Merchants/useGetMerchantQuery";
import { merchantsListKeys } from "Api/Queries/Merchants/useGetMerchantsQuery";
import {
  type ApiProblemDetails,
  type FetchResponse,
  postMerchantsPublicIDCancelAccess,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Hunter.Merchants.Detail;

type Parameters = { merchantPublicID: string };

export type CancelMerchantAccessError = FetchResponse<
  ApiProblemDetails,
  403 | 404
>;

type Props = {
  onSuccess?: () => void;
  onError?: (error: CancelMerchantAccessError) => void;
};

export const useCancelMerchantAccessMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      ({ merchantPublicID }: Parameters) =>
        postMerchantsPublicIDCancelAccess(merchantPublicID),
    ),
    onSuccess: (_: unknown, { merchantPublicID }: Parameters) => {
      queryClient.invalidateQueries({ queryKey: merchantsListKeys.all });
      queryClient.invalidateQueries({
        queryKey: merchantKeys.byMerchantPublicId(merchantPublicID),
      });
      toast.success(t(RESOURCES.CancelAccessSuccess));
      onSuccess?.();
    },
    onError: (error: CancelMerchantAccessError) => {
      onError?.(error);
      // Only the attributing Hunter or an Admin may cancel (backend NotInvitationInviter → 403).
      // Surface that reason instead of the generic error so a non-inviting hunter knows why.
      if (error.status === 403) {
        toast.error(t(RESOURCES.CancelAccessForbidden));
        return;
      }
      toast.error(t(RESOURCES.CancelAccessError));
    },
  });
};
